import React from 'react'
import { LanguageProvider } from '../components/LanguageContext'
import { getTranslation } from '../translations'
import ReportHeader from '../components/ReportHeader'
import UserData from '../components/UserData'
import SoilAnalysis from '../components/SoilAnalysis'
import DetailedInsights from '../components/DetailedInsights'
import FertilizerRecommendation from '../components/FertilizerRecommendation'
import DerivedInsights from '../components/DerivedInsights'
import CropSpecificTips from '../components/CropSpecificTips'
import Disclaimer from '../components/Disclaimer'
// import NaturalRecommendation from '../components/partner-specific/NaturalRecommendation'
import Technic from '../components/partner-specific/Technic'
import WaterAnalysis from '../components/WaterAnalysis'
import WaterDisclaimer from '../components/WaterDisclaimer'
import { hasPermission } from '../../../hooks/usePermission'
import WaterRecommendation from '../components/WaterRecommendation'

// Helper to map SoilAnalysis status to DetailedInsights level
// SoilAnalysis: 'low' | 'high' | 'ok' | 'na'
// DetailedInsights: 'low' | 'normal' | 'high'
function mapStatusToLevel(actual, min, max) {
  if (actual == null || Number.isNaN(actual)) return 'medium'
  if (min == null || max == null) return 'medium'
  if (actual < min) return 'low'
  if (actual > max) return 'high'
  return 'medium' // 'ok' maps to 'normal'
}

// Helper to get multilingual value from API response
function getMultilingualValue(value, language) {
  if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === '') {
    return '';
  }
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[language] || value.english || Object.values(value)[0] || ''
  }
  return String(value)
}

// Transform API report data to user data format
function transformUserData(apiData, language) {
  if (!apiData?.report) return null

  const report = apiData.report
  const reportDateRaw = report.created_at || report.collection_date || null
  const reportDate = (() => {
    if (!reportDateRaw) return '-'
    const d = new Date(reportDateRaw)
    if (isNaN(d.getTime())) return '-'
    const localeMap = {
      english: 'en-IN',
      hindi: 'hi-IN',
      gujarati: 'gu-IN',
      telugu: 'te-IN',
      punjabi: 'pa-IN',
      marathi: 'mr-IN',
      kannada: 'kn-IN',
      tamil: 'ta-IN',
      odia: 'or-IN',
      bengali: 'bn-IN',
    };

    const locale = localeMap[language] || 'en-IN';

    return d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  })()
  return {
    name: report.name || '',
    mobile: report.mobile_no || '',
    address: `${report.village || ''}, ${report.address || ''}, ${report.city || ''}, ${report.state || ''}`.replace(/^,\s*|,\s*$/g, ''),
    cityState: `${report.city || ''}, ${report.state || ''}`.replace(/^,\s*|,\s*$/g, ''),
    previousCrop: report.previous_crop || '',
    farmSize: `${report.farm_size || ''} ${report.farm_size_unit || ''}`.trim(),
    farmName: report.farm_name || '',
    nextCrop: report.next_crop || '',
    sampleCode: report.sample_code || '',
    treatmentType: report.treatment || '-',
    reportDate,
    sampleType: report.sample_type || '-'
  }
}

// Transform API soil data to analysis format
function transformSoilData(apiData) {
  if (!apiData?.report) return { rows: [], derived: [] }

  const report = apiData.report

  // Helper to create soil parameter object
  const createParam = (parameter, idealText, min, max, actualValue, actualNumber) => ({
    parameter,
    idealText,
    min,
    max,
    actualText: actualValue,
    actualNumber
  })

  // Helper to safely format parameter values
  function valueOrDash(val, suffix = '') {
    if (val === undefined || val === null || val === '') return '-'
    if (isNaN(Number(val))) {
      return String(val);
    }
    return suffix ? `${val} ${suffix}` : `${val}`
  }

  function getSocValue(ocValue) {
    if(ocValue.toUpperCase() === 'BODL' || ocValue <= 0) return 'BODL';
    return ocValue * 1.724;
  }

  function formatDetectionLimitValue(value, limit, suffix = 'mg/kg') {
    if (value === undefined || value === null || value === '') return '-';

    const numericValue = Number(value);

    if (!Number.isNaN(numericValue) && numericValue < limit) {
      return `<${limit} ${suffix}`;
    }

    if (String(value).toUpperCase() === 'BODL') {
      return `<${limit} ${suffix}`;
    }

    return `${value} ${suffix}`;
  }

  function getDetectionLimitNumber(value, limit) {
    if (value === undefined || value === null || value === '') {
      return NaN;
    }

    const stringValue = String(value).trim();

    if (stringValue.startsWith('<')) {
      const numericValue = parseFloat(stringValue.substring(1));
      return Number.isNaN(numericValue) ? NaN : numericValue;
    }

    if (stringValue.toUpperCase() === 'BODL') {
      return limit;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? NaN : numericValue;
  }

  const rows = [
    createParam('pH', '6.5 - 7.5', 6.5, 7.5, valueOrDash(report.ph), parseFloat(report.ph)),
    createParam('EC (dS/m)', '0.8 - 2.0', 0.8, 2.0, valueOrDash(report.ec), parseFloat(report.ec)),
    createParam('Soil Organic Carbon (Oxidizable)', '0.5 - 0.75 %', 0.5, 0.75, valueOrDash(report.oc, '%'), parseFloat(report.oc)),
    createParam('Soil Organic Matter (%)', '2.0 - 4.0 %', 2, 4, (() => { const socValue = getSocValue(report.oc); if (socValue === 'BODL') { return 'BODL'; } return valueOrDash(socValue.toFixed(2), '%'); })(), getSocValue(report.oc) === 'BODL' ? NaN : parseFloat(getSocValue(report.oc).toFixed(2))),
    createParam('Available Nitrogen', '280 - 560 kg/ha', 280, 560, valueOrDash(report.nitrogen, 'kg/ha'), parseFloat(report.nitrogen)),
    createParam('Available Phosphorus', '10 - 25 kg/ha', 10, 25, valueOrDash(report.phosphorus, 'kg/ha'), parseFloat(report.phosphorus)),
    createParam('Available Potassium', '110 - 280 kg/ha', 110, 280, valueOrDash(report.potassium, 'kg/ha'), parseFloat(report.potassium)),
    createParam('Sulphur', '10 - 20 mg/kg', 10, 20, formatDetectionLimitValue(report.sulphur, 2.85), getDetectionLimitNumber(report.sulphur, 2.85)),
    createParam('Magnesium', '180 - 540 ppm', 180, 540, valueOrDash(report.magnesium, 'ppm'), parseFloat(report.magnesium)),
    createParam('Calcium', '400 - 1200 ppm', 400, 1200, valueOrDash(report.calcium, 'ppm'), parseFloat(report.calcium)),
    createParam('Zinc', '0.5 - 1.0 mg/kg', 0.5, 1.0, valueOrDash(report.zinc, 'mg/kg'), parseFloat(report.zinc)),
    createParam('Iron', '5.0 - 10.0 mg/kg', 5, 10, valueOrDash(report.iron, 'mg/kg'), parseFloat(report.iron)),
    createParam('Copper', '0.2 - 0.4 mg/kg', 0.2, 0.4, formatDetectionLimitValue(report.copper, 0.008), getDetectionLimitNumber(report.copper, 0.008)),
    createParam('Boron', '0.1 - 0.5 mg/kg', 0.1, 0.5, valueOrDash(report.boron, 'mg/kg'), parseFloat(report.boron)),
  ]

  // --- CEC from OM Calculation (from image) ---
  // f(pH) scale as per user table
  function getFPH(ph) {
    if (ph == null || isNaN(ph)) return null
    if (ph < 5.0) return 1.5
    if (ph >= 5.0 && ph <= 5.5) return 2.0
    if (ph > 5.5 && ph <= 6.0) return 2.5
    if (ph > 6.0 && ph <= 6.5) return 3.0
    if (ph > 6.5 && ph <= 7.0) return 3.5
    if (ph > 7.0 && ph <= 7.5) return 4.0
    if (ph > 7.5) return 4.5
    return null
  }

  // Calculate derived parameters
  const oc = parseFloat(report.oc) || 0
  // Updated C:N Ratio:
  // Total N% = OC% / M
  // C:N Ratio = OC% / Total N%
  // M: OC < 0.5 => 9.5, 0.5-0.75 => 11, OC > 0.75 => 12
  const getMFromOC = (ocValue) => {
    if (ocValue < 0.5) return 9.5
    if (ocValue <= 0.75) return 11
    return 12
  }
  const m = getMFromOC(oc)
  const totalNPercent = m > 0 ? Number((oc / m).toFixed(3)) : 0
  const cnRatio = totalNPercent > 0 ? oc / totalNPercent : 0

  // Calculate Total CEC using new formula
  const ph = parseFloat(report.ph)
  const fph = getFPH(ph)
  const potassium = parseFloat(report.potassium) || 0
  const calcium = parseFloat(report.calcium) || 0
  const magnesium = parseFloat(report.magnesium) || 0

  // Base CEC from exchangeable cations
  const newK = potassium * 0.0009486
  const newCa = calcium * 0.00499
  const newMg = magnesium * 0.00823
  const baseCEC = newK + newCa + newMg

  // SOC CEC from organic carbon and pH factor
  const socCEC = oc && fph ? oc * fph : 0

  // Total CEC
  const totalCEC = baseCEC + socCEC

  // Calculate Ca:Mg Ratio
  const caMgRatio = magnesium > 0 ? (calcium * 121.5) / (magnesium * 200) : 0

  // Calculate K:Mg Ratio
  const kMgRatio = magnesium > 0 ? (potassium * 121.5) / (magnesium * 391) : 0

  // Helper to format derived parameter value: show "0" if value is 0, "-" if missing/null
  const formatDerivedValue = (value, formatFunc) => {
    if (value === null || value === undefined || isNaN(value)) return '-'
    if (value === 0) return formatFunc(0)
    return formatFunc(value)
  }

  const derived = [
    createParam('C:N Ratio', '8:1-12:1', 8, 12, formatDerivedValue(cnRatio, (v) => `${v.toFixed(2)}:1`), cnRatio),
    createParam(
      'CEC',
      '10-20 cmol(+)/kg',
      10,
      20,
      formatDerivedValue(totalCEC, (v) => `${v.toFixed(2)} cmol(+)/kg`),
      totalCEC
    ),
    createParam('Ca:Mg Ratio', '5:1-7:1', 5, 7, formatDerivedValue(caMgRatio, (v) => `${v.toFixed(1)}:1`), caMgRatio),
    createParam('K:Mg Ratio', '0.5:1-0.7:1', 0.5, 0.7, formatDerivedValue(kMgRatio, (v) => `${v.toFixed(1)}:1`), kMgRatio),
  ]

  return { rows, derived }
}

// Transform API Water Data to analysis format
function transformWaterData(apiData) {
  if (!apiData?.report) return { rows: [] }

  const report = apiData.report

  // Helper to create soil parameter object
  const createParam = (parameter, idealText, min, max, actualValue, actualNumber) => ({
    parameter,
    idealText,
    min,
    max,
    actualText: actualValue,
    actualNumber
  })

  // Helper to safely format parameter values
  function valueOrDash(val, suffix = '') {
    if (val === undefined || val === null || val === '') return '-'
    if (isNaN(Number(val))) {
      return String(val);
    }
    return suffix ? `${val} ${suffix}` : `${val}`
  }

  const rows = [
    createParam('pH', '6.0 - 8.0', 6, 8, valueOrDash(report.ph), parseFloat(report.ph)),
    createParam('EC (dS/m)', '0 - 2', 0, 2, valueOrDash(report.ec), parseFloat(report.ec)),
    createParam('Nitrate', '< 30 ppm', 0, 30, valueOrDash(report.nitrate, 'ppm'), parseFloat(report.nitrate)),
    createParam('Ammonia', '< 10 ppm', 0, 10, valueOrDash(report.ammonia, 'ppm'), parseFloat(report.ammonia)),
    createParam('Phosphorus', '< 5 ppm', 0, 5, valueOrDash(report.phosphorus, 'ppm'), parseFloat(report.phosphorus)),
    createParam('Potassium', '< 40 ppm', 0, 40, valueOrDash(report.potassium, 'ppm'), parseFloat(report.potassium)),
    createParam('Sulphur', '< 50 ppm', 0, 50, valueOrDash(report.sulphur, 'ppm'), parseFloat(report.sulphur)),
    createParam('Boron', '< 2 ppm', 0, 2, valueOrDash(report.boron, 'ppm'), parseFloat(report.boron)),
    createParam('Zinc', '< 5 ppm', 0, 5, valueOrDash(report.zinc, 'ppm'), parseFloat(report.zinc)),
    createParam('Iron', '< 20 ppm', 0, 20, valueOrDash(report.iron, 'ppm'), parseFloat(report.iron)),
    createParam('Calcium', '< 200 ppm', 0, 200, valueOrDash(report.calcium, 'ppm'), parseFloat(report.calcium)),
    createParam('Magnesium', '< 100 ppm', 0, 100, valueOrDash(report.magnesium, 'ppm'), parseFloat(report.magnesium)),
    createParam('Copper', '< 1 ppm', 0, 1, valueOrDash(report.copper, 'ppm'), parseFloat(report.copper)),
    createParam('Carbonate', '< 10 ppm', 0, 10, valueOrDash(report.carbonate, 'ppm'), parseFloat(report.carbonate)),
    createParam('Bicarbonate', '< 180 ppm', 0, 180, valueOrDash(report.bicarbonate, 'ppm'), parseFloat(report.bicarbonate)),
    createParam('TDS', '< 1000 ppm', 0, 1000, valueOrDash(report.tds, 'ppm'), parseFloat(report.tds)),

  ]

  return { rows }
}

// Transform API recommendations to fertilizer stages format
function transformRecommendations(apiData, language) {
  if (!apiData?.recommData?.recommendation?.recommendations) return null

  const recommendations = apiData.recommData.recommendation.recommendations

  const formatDose = (dose, unit) => {
    const cleanDose = (dose === null || dose === undefined || dose === 'null') ? '' : dose;
    const cleanUnit = getMultilingualValue(unit, language);
    return `${cleanDose} ${cleanUnit}`.trim();
  };

  // Handle the case where recommendations is an object with language keys (en, hi, pb, tel)
  if (typeof recommendations === 'object' && !Array.isArray(recommendations)) {
    const langKey =
    language === 'hindi' ? 'hi' :
    language === 'punjabi' ? 'pb' :
    language === 'telugu' ? 'tel' :
    language === 'gujarati' ? 'gu' :
    language === 'marathi' ? 'mr' :
    language === 'kannada' ? 'kn' :
    language === 'tamil' ? 'ta' :
    language === 'odia' ? 'od' : 
    language === 'bengali' ? 'bn' : 
    'en';

    const langRecommendations = recommendations[langKey]
    if (!langRecommendations) return null

    // Convert the language-specific recommendations object to array format
    const recommendationArray = Object.values(langRecommendations)
      .filter(rec => rec.to_apply) // Only include recommendations that have values
      .map((rec, index) => ({
        order: index + 1,
        orderName: rec.parameter || `Stage ${index + 1}`,
        prescription: [{
          input: rec.to_apply,
          perSessionDose: rec.quantity,
          unit: rec.unit,
          method: rec.application
        }]
      }))

    return recommendationArray.map(stage => ({
      stage: getMultilingualValue(stage.orderName, language),
      inputs: stage.prescription.map(item => ({
        input: getMultilingualValue(item.input, language),
        dose: formatDose(item.perSessionDose, item.unit),
        method: getMultilingualValue(item.method, language)
      }))
    }))
  }

  // Handle the case where recommendations is already an array (legacy format)
  if (Array.isArray(recommendations)) {
    return [...recommendations]
      .sort((a, b) => a.order - b.order)
      .map(stage => ({
        stage: getMultilingualValue(stage.orderName, language),
        inputs: stage.prescription.map(item => ({
          input: getMultilingualValue(item.input, language),
          dose: formatDose(item.perSessionDose, item.unit),
          method: getMultilingualValue(item.method, language)
        }))
      }))
  }

  return null
}

function transformWaterRecommendations(apiData, language) {
  if (!apiData?.recommData?.recommendation?.corrections) return []

  const corrections = apiData.recommData.recommendation.corrections;

  return corrections.map(item => ({
    parameterName: item.parameter, // e.g., 'ph', 'ec'
    input: getMultilingualValue(item.input, language),
    dose: getMultilingualValue(item.dose, language),
    method: getMultilingualValue(item.method, language)
  }))
}

// All static data removed - now using API data only

// Build insight items using translations
function buildInsightItems(t, rows) {
  // Helper function to safely get row data
  const getRowData = (index) => {
    const row = rows[index]
    return row ? {
      actualNumber: row.actualNumber,
      min: row.min,
      max: row.max
    } : { actualNumber: null, min: null, max: null }
  }

  return [
    { name: 'pH', level: mapStatusToLevel(getRowData(0).actualNumber, getRowData(0).min, getRowData(0).max), meaning: t.phMeaning, function: t.phFunction },
    { name: 'EC (dS/m)', level: mapStatusToLevel(getRowData(1).actualNumber, getRowData(1).min, getRowData(1).max), meaning: t.ecMeaning, function: t.ecFunction },
    { name: 'OC (%)', level: mapStatusToLevel(getRowData(2).actualNumber, getRowData(2).min, getRowData(2).max), meaning: t.ocMeaning, function: t.ocFunction },
    { name: 'Nitrogen', level: mapStatusToLevel(getRowData(4).actualNumber, getRowData(4).min, getRowData(4).max), meaning: t.nitrogenMeaning, function: t.nitrogenFunction },
    { name: 'Phosphorus', level: mapStatusToLevel(getRowData(5).actualNumber, getRowData(5).min, getRowData(5).max), meaning: t.phosphorusMeaning, function: t.phosphorusFunction },
    { name: 'Potassium', level: mapStatusToLevel(getRowData(6).actualNumber, getRowData(6).min, getRowData(6).max), meaning: t.potassiumMeaning, function: t.potassiumFunction },
    { name: 'Sulphur', level: mapStatusToLevel(getRowData(7).actualNumber, getRowData(7).min, getRowData(7).max), meaning: t.sulphurMeaning, function: t.sulphurFunction },
    { name: 'Magnesium', level: mapStatusToLevel(getRowData(8).actualNumber, getRowData(8).min, getRowData(8).max), meaning: t.magnesiumMeaning, function: t.magnesiumFunction },
    { name: 'Calcium', level: mapStatusToLevel(getRowData(9).actualNumber, getRowData(9).min, getRowData(9).max), meaning: t.calciumMeaning, function: t.calciumFunction },
    { name: 'Zinc', level: mapStatusToLevel(getRowData(10).actualNumber, getRowData(10).min, getRowData(10).max), meaning: t.zincMeaning, function: t.zincFunction },
    { name: 'Iron', level: mapStatusToLevel(getRowData(11).actualNumber, getRowData(11).min, getRowData(11).max), meaning: t.ironMeaning, function: t.ironFunction },
    { name: 'Copper', level: mapStatusToLevel(getRowData(12).actualNumber, getRowData(12).min, getRowData(12).max), meaning: t.copperMeaning, function: t.copperFunction },
    { name: 'Boron', level: mapStatusToLevel(getRowData(13).actualNumber, getRowData(13).min, getRowData(13).max), meaning: t.boronMeaning, function: t.boronFunction },
  ]
}

function buildDerivedInsights(t, derived) {
  // Helper function to safely get derived data
  const getDerivedData = (index) => {
    const item = derived[index]
    return item ? {
      actualNumber: item.actualNumber,
      min: item.min,
      max: item.max
    } : { actualNumber: null, min: null, max: null }
  }

  return [
    { name: 'C:N Ratio', level: mapStatusToLevel(getDerivedData(0).actualNumber, getDerivedData(0).min, getDerivedData(0).max), meaning: t.cnRatioMeaning, function: t.cnRatioFunction },
    { name: 'CEC Tendency', level: mapStatusToLevel(getDerivedData(1).actualNumber, getDerivedData(1).min, getDerivedData(1).max), meaning: t.cecTendencyMeaning, function: t.cecTendencyFunction },
    { name: 'Ca:Mg Ratio', level: mapStatusToLevel(getDerivedData(2).actualNumber, getDerivedData(2).min, getDerivedData(2).max), meaning: t.caMgRatioMeaning, function: t.caMgRatioFunction },
    { name: 'K:Mg Ratio', level: mapStatusToLevel(getDerivedData(3).actualNumber, getDerivedData(3).min, getDerivedData(3).max), meaning: t.kMgRatioMeaning, function: t.kMgRatioFunction },
  ]
}

// All static data removed - now using API data only

// Build derived indicators using translations
function buildDerivedIndicators(t) {
  return [
    {
      indicator: 'C:N Ratio',
      lowEffect: t.cnRatioLowEffect,
      highEffect: t.cnRatioHighEffect,
    },
    {
      indicator: 'CEC Tendency',
      lowEffect: t.cecTendencyLowEffect,
      highEffect: t.cecTendencyHighEffect,
    },
    {
      indicator: 'Ca:Mg Ratio',
      lowEffect: t.caMgRatioLowEffect,
      highEffect: t.caMgRatioHighEffect,
    },
    {
      indicator: 'K:Mg Ratio',
      lowEffect: t.kMgRatioLowEffect,
      highEffect: t.kMgRatioHighEffect,
    },
  ]
}

// Build nutrient blocking data using translations
function buildNutrientBlocking(t) {
  return [
    { nutrient: 'Phosphorus (P)', blocks: t.phosphorusBlocks },
    { nutrient: 'Potassium (K)', blocks: t.potassiumBlocks },
    { nutrient: 'Nitrogen (N)', blocks: t.nitrogenBlocks },
  ]
}

export default function FullReport({ data, language = 'english', isPublic }) {
  const t = getTranslation(language)
  const isWaterReport = data?.report?.sample_type === "WATER";

  const isUnderReview = data?.report?.report_status === 'R';

  // Transform all data from API
  const userData = transformUserData(data, language)
  const analysisData = isWaterReport
    ? transformWaterData(data)
    : transformSoilData(data);

  const fertilizerStages = !isWaterReport
    ? transformRecommendations(data, language)
    : [];

  const waterCorrections = isWaterReport 
    ? transformWaterRecommendations(data, language)
    : [];

  const actualCropName = !isWaterReport
    ? data?.report?.next_crop
    : null;

  // Partner / association info:
  // - If partner logo exists: header component will show logo + partner name on the left and
  //   "In association with Ekosight Technologies Private Limited" on the right.
  // - If partner logo does NOT exist but partner name exists: header shows Soil Doctor on the left
  //   and "In association with {Partner name}" on the right.
  // - If partner logo does NOT exist and partner name does NOT exist: header shows Soil Doctor on the left
  //   and "In association with Ekosight Technologies Private Limited" on the right (fallback).
  const partnerLogoUrl = data?.report?.entity_logo || null
  const entityName = data?.report?.entity_name || null

  // Use a clean partnerName (no Ekosight fallback here); this is passed to disclaimers.
  const partnerName = (entityName && entityName !== 'Ekosight Technologies Private Limited')
    ? entityName
    : null

  // Association text for the header:
  // - When logo exists: use partnerName (left shows partner name, right shows Ekosight)
  // - When logo does NOT exist: use partnerName if exists, else fallback to Ekosight
  //   (ensures right side is never blank)
  const association = partnerLogoUrl
    ? partnerName
    : (partnerName || 'Ekosight Technologies Private Limited')

  // Normalize crop name to handle casing, whitespace, and synonyms
  const normalizeCropName = (name) => {
    if (!name || typeof name !== 'string') return ''
    const lower = name.toLowerCase().trim()
    // If API sends compound strings like "rice/paddy" or "maize (corn)", pick known keyword
    if (lower.includes('potato')) return 'potato'
    if (lower.includes('wheat')) return 'wheat'
    if (lower.includes('maize') || lower.includes('corn')) return 'maize'
    if (lower.includes('sugarcane')) return 'sugarcane'
    if (lower.includes('mustard')) return 'mustard'
    if (lower.includes('rice') || lower.includes('paddy')) return 'rice'
    return lower
  }
  const normalizedCropName = normalizeCropName(actualCropName)

  // Build dynamic data using translations
  const insightItems = !isWaterReport
    ? buildInsightItems(t, analysisData.rows)
    : [];

  const derivedInsights = !isWaterReport
    ? buildDerivedInsights(t, analysisData.derived)
    : [];

  const derivedIndicators = !isWaterReport
    ? buildDerivedIndicators(t)
    : [];

  const nutrientBlocking = !isWaterReport
    ? buildNutrientBlocking(t)
    : [];

  const naturalRecommendationAdminIds = ['test company', 'Shramik Bharti'];
  const customSoilDisclaimer = data?.report?.custom_disclaimer_soil || null;
  const customWaterDisclaimer = data?.report?.custom_disclaimer_water || null;

  return (
    <LanguageProvider language={language}>
      <ReportHeader
        association={association}
        language={language}
        partnerLogoUrl={partnerLogoUrl}
      />
      <UserData data={userData} language={language} />
      <div className='px-2'>
        {isUnderReview && (
          <div
            className="alert alert-warning my-4 p-4 rounded-3"
            style={{
              backgroundColor: '#fff8e6',
              borderColor: '#ffeeba',
              color: '#856404',
            }}
          >
            <h5 className="alert-heading fw-bold mb-3">
              🚨 <strong>{t.underReviewHeading}</strong>
            </h5>

            <p className="mb-2 fw-bold">
              {t.underReviewBody}
            </p>
          </div>
        )}
        {isWaterReport ? (
          <>
            <WaterAnalysis rows={analysisData.rows} language={language} />
            <WaterRecommendation corrections={waterCorrections} language={language} />
            {(isPublic || hasPermission('reports:disclaimer')) && (
              <WaterDisclaimer language={language} partnerName={partnerName} customText={customWaterDisclaimer} />
            )}
          </>
        ) : (
          <>
            <SoilAnalysis rows={analysisData.rows} derivedRows={analysisData.derived} language={language} />
            <DetailedInsights items={insightItems} derivedItems={derivedInsights} language={language} />
            <FertilizerRecommendation stages={fertilizerStages} language={language} />
            <DerivedInsights indicators={derivedIndicators} blockingData={nutrientBlocking} language={language} />
            <CropSpecificTips cropName={normalizedCropName} language={language} isPublic={isPublic} />
            {entityName && naturalRecommendationAdminIds.includes(entityName.toLowerCase().trim()) && (
              <>
                {/* <NaturalRecommendation language={language}/> */}
                <Technic language={language} />
              </>
            )}

            {(isPublic || hasPermission('reports:disclaimer')) && (
              <Disclaimer language={language} partnerName={partnerName} customText={customSoilDisclaimer}/>
            )}
          </>
        )}
      </div>
    </LanguageProvider>
  )
}
